import React from "react";

const DataProcessingAgreement: React.FC = () => {
  return (
    <div className="bg-gray-100 p-4 sm:p-8 md:p-12 lg:p-16">
      <div className="bg-white max-w-4xl mx-auto rounded-xl shadow-2xl p-6 sm:p-8 md:p-12 lg:p-16">
        {/* Title and Introduction Section */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-2 leading-tight">Waivera DATA PROCESSING AGREEMENT (HIPAA)</h1>
        <p className="text-gray-500 text-sm mb-8 font-medium">Last Updated: June 3, 2025</p>
        
        <div className="mb-12 text-gray-700 space-y-6 text-sm sm:text-base">
          <p>
            This Data Processing Agreement (“DPA”) is entered into between Waivera
            (“Business Associate,” “we,” “us,” or “our”) and the user (“Covered Entity,” “you,”
            or “your”) to ensure compliance with the Health Insurance Portability and
            Accountability Act (HIPAA) and related regulations.
          </p>
          <p>
            This agreement governs the processing, storage, and transmission of Protected
            Health Information (PHI) through the Waivera platform. Waivera facilitates
            electronic signature transactions for Medicaid case managers, waiver clients, in-
            home providers, and state representatives. As a Business Associate, Waivera is
            committed to implementing appropriate safeguards to protect PHI in accordance
            with HIPAA Privacy, Security, and Breach Notification Rules.
          </p>
          <p>
            By using Waivera, you acknowledge that this agreement forms part of the Terms of
            Service and that your continued use of the platform constitutes acceptance of these
            terms.
          </p>
          <p>For any inquiries regarding this agreement, contact <a href="mailto:notifications@waivera.com" className="text-blue-600 hover:underline">notifications@waivera.com</a>.</p>
        </div>

        {/* Definitions Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-book-open text-purple-500"></i> Definitions
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>For the purposes of this Agreement, the following terms shall have the meanings
                set forth below:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Protected Health Information (PHI):</strong> This refers to any health-related data processed
                under HIPAA, including but not limited to medical records, patient identifiers, and
                any information related to the provision of healthcare services.</li>
              <li><strong>Business Associate:</strong> This refers to Waivera, acting as a service provider that
                processes, transmits, or stores PHI on behalf of Covered Entities in compliance with
                HIPAA regulations.</li>
              <li><strong>Covered Entity:</strong> This refers to users of Waivera, including Medicaid case managers,
                healthcare providers, waiver clients, and other authorized entities that handle PHI
                and require compliance with HIPAA.</li>
              <li><strong>Sub processors:</strong> This refers to third-party service providers engaged by Waivera to
                process PHI on its behalf, ensuring compliance with all applicable security and
                privacy regulations.</li>
            </ul>
          </div>
        </div>

        {/* Scope & Purpose Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-bullseye text-indigo-500"></i> Scope & Purpose
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              This Agreement governs how SPROUT, operated by Waivera, processes, stores,
              and transmits Protected Health Information (PHI) in compliance with the HIPAA
              Security & Privacy Rules.
            </p>
            <p>
              The processing of PHI through Waivera is strictly limited to what is necessary for
              facilitating electronic signature transactions and secure document storage. HEAI,
              Inc. shall not use or disclose PHI for any purpose other than those explicitly outlined
              in this Agreement, except as required by law or with prior written consent from the
              Covered Entity.
            </p>
            <p>
              All PHI handled by Waivera shall be safeguarded using industry-standard
              encryption, access controls, and audit logs to ensure its confidentiality, integrity,
              and availability in accordance with HIPAA regulations.
            </p>
          </div>
        </div>
        
        {/* Obligations of Waivera (BUSINESS ASSOCIATE) Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-handshake text-pink-500"></i> Obligations of Waivera (Business Associate)
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <h3 className="font-bold text-gray-800 text-lg">Confidentiality & Security</h3>
            <p>
              Waivera is committed to safeguarding Protected Health Information (PHI) by implementing industry-standard encryption, access controls, and audit trails to prevent unauthorized access, disclosure, or misuse. All PHI processed through SPROUT shall be protected in compliance with HIPAA Security & Privacy Rules.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">Permitted Uses</h3>
            <p>
              Waivera shall only use PHI as necessary to facilitate the electronic signature transactions and secure document storage functions of SPROUT. Any processing of PHI shall be limited to fulfilling the obligations outlined in this Agreement and in compliance with HIPAA.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">Prohibited Actions</h3>
            <p>
              Waivera shall not sell, rent, disclose, or use PHI for any unauthorized purpose. Waivera shall not allow unauthorized access, transfer, or modification of PHI unless required by law or explicitly authorized in writing by the Covered Entity. Any breach of these obligations will be addressed in accordance with HIPAA regulations.
            </p>
          </div>
        </div>

        {/* Obligations of USERS (COVERED ENTITIES) Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-users text-blue-500"></i> Obligations of Users (Covered Entities)
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <h3 className="font-bold text-gray-800 text-lg">Legally Obtained PHI</h3>
            <p>
              Users, as Covered Entities, must ensure that any Protected Health Information (PHI) provided to Waivera is lawfully obtained and processed in compliance with HIPAA, Medicaid regulations, and any other applicable state or federal laws. Users are responsible for ensuring that they have the necessary authorizations, consents, or legal rights to share and process PHI within the platform.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">Safeguarding Access Credentials</h3>
            <p>
              Users must implement appropriate security measures to protect their account credentials, login information, and access rights to SPROUT. This includes preventing unauthorized access, sharing, or disclosure of their login credentials and ensuring that only authorized personnel have access to PHI within the platform. Any suspected or actual security breach must be reported to Waivera immediately to mitigate potential risks.
            </p>
          </div>
        </div>

        {/* Data Security Measures Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-shield-alt text-green-500"></i> Data Security Measures
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <h3 className="font-bold text-gray-800 text-lg">Encryption of PHI</h3>
            <p>
              Waivera ensures that all Protected Health Information (PHI) processed through SPROUT is encrypted both in transit and at rest using industry-standard encryption protocols. This protects PHI from unauthorized access, interception, or compromise during transmission and storage.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">Role-Based Access Control (RBAC)</h3>
            <p>
              Access to PHI within Waivera is restricted through role-based access controls (RBAC), ensuring that only authorized personnel can view, process, or modify PHI. Users are granted access based on their roles, with least privilege principles applied to minimize unnecessary data exposure.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">Secure Audit Logs</h3>
            <p>
              Waivera maintains detailed audit logs that track who accessed, modified, or transmitted PHI, along with timestamps and relevant activity details. These logs are securely stored and monitored to ensure compliance with HIPAA Security & Privacy Rules and to facilitate auditing, compliance verification, and incident response.
            </p>
          </div>
        </div>
        
        {/* Data Breach Notification Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-exclamation-triangle text-yellow-500"></i> Data Breach Notification
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <h3 className="font-bold text-gray-800 text-lg">Notification Obligation</h3>
            <p>
              In the event of a data breach involving Protected Health Information (PHI), Waivera shall notify affected users within [Insert Timeframe] as required by HIPAA’s Breach Notification Rule and applicable state laws. The notification will include details about the nature and scope of the breach, the type of PHI involved, and any potential risks to affected individuals.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">Mitigation and Response Measures</h3>
            <p>
              Upon discovering a breach, Waivera will take immediate action to contain and mitigate the incident. This includes:
              Conducting a forensic investigation to determine the cause and scope of the breach.
              Implementing corrective security measures to prevent further unauthorized access.
              Notifying affected parties, including users, regulatory authorities, and other required entities, in accordance with HIPAA and applicable state laws.
              Providing guidance to affected users on steps they should take to protect their information, such as monitoring accounts for suspicious activity or updating security credentials.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">Compliance with Legal and Regulatory Requirements</h3>
            <p>
              Waivera will document and report the breach in compliance with HIPAA, the HITECH Act, and any applicable state Medicaid regulations. If necessary, public notifications will be made in accordance with regulatory requirements.
            </p>
          </div>
        </div>

        {/* Data Retention & Deletion Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-archive text-gray-500"></i> Data Retention & Deletion
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <h3 className="font-bold text-gray-800 text-lg">Retention of PHI</h3>
            <p>
              Waivera will retain Protected Health Information (PHI) within SPROUT for as long as necessary to fulfill the electronic signature transactions, document storage, and compliance requirements under HIPAA and applicable Medicaid regulations. PHI will be stored in accordance with legal and regulatory retention periods, after which it will be securely deleted or archived as required by law.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">User Requests for PHI Deletion</h3>
            <p>
              Users may submit a request to Waivera for the deletion of PHI when it is no longer required for legal, regulatory, or business purposes. Requests must be made in writing to <a href="mailto:notifications@waivera.com" className="text-blue-600 hover:underline">notifications@waivera.com</a>, and Waivera will review and process the request in compliance with HIPAA’s right-to-access and right-to-delete provisions where applicable.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">HIPAA-Compliant PHI Disposal</h3>
            <p>
              When PHI is no longer needed or upon the termination of a user’s account, Waivera will ensure its secure disposal in compliance with HIPAA regulations. This includes:
              Permanent data deletion using industry-standard secure deletion methods.
              Secure shredding or destruction of any physical records, if applicable.
              Ensuring that any sub-processors handling PHI also comply with proper disposal procedures.
              All PHI deletion and disposal processes will be documented and verified to ensure compliance with HIPAA and applicable Medicaid regulations.
            </p>
          </div>
        </div>
        
        {/* Sub-processors Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-people-carry text-red-500"></i> Sub-processors
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <h3 className="font-bold text-gray-800 text-lg">Disclosure of Third-Party Sub-processors</h3>
            <p>
              Waivera may engage third-party sub-processors to assist in processing Protected Health Information (PHI) on behalf of SPROUT. These sub-processors may provide cloud storage, security monitoring, encryption, or other essential services necessary for platform functionality. A list of sub-processors will be made available upon request, and Waivera will ensure that all sub-processors operate under legally binding agreements that meet HIPAA compliance requirements.
            </p>
            <h3 className="font-bold text-gray-800 text-lg">HIPAA Compliance and Security Obligations</h3>
            <p>
              Any sub-processors engaged by Waivera must comply with HIPAA’s Security, Privacy, and Breach Notification Rules and implement appropriate technical, administrative, and physical safeguards to protect PHI. Waivera will:
              Conduct due diligence and security assessments before engaging any sub-processor.
              Require all sub-processors to sign a Business Associate Agreement (BAA) ensuring their compliance with HIPAA.
              Continuously monitor sub-processors’ compliance and security practices to mitigate risks related to PHI processing.
              Waivera remains fully responsible for the actions of its sub-processors regarding PHI handling and ensures that all subcontracted services maintain the same level of data protection as required under this agreement.
            </p>
          </div>
        </div>

        {/* Termination & Data Return Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-times-circle text-gray-500"></i> Termination & Data Return
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <h3 className="font-bold text-gray-800 text-lg">Termination Conditions</h3>
            <p>
              This Agreement may be terminated under the following conditions:
              By either party with prior written notice if the other party materially breaches any obligation under this Agreement and fails to remedy the breach within a reasonable timeframe.
              Automatically upon termination of the user's access to SPROUT or discontinuation of services by Waivera..
              If required by law or regulatory authorities, including any changes in HIPAA regulations that impact the validity of this Agreement.
            </p>
          </div>
        </div>

        {/* Governing Law Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-balance-scale text-yellow-500"></i> Governing Law
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              This Agreement shall be governed by and interpreted in accordance with the Health
              Insurance Portability and Accountability Act (HIPAA) and its implementing
              regulations. Additionally, this Agreement is subject to all applicable state laws and
              Medicaid regulations in the jurisdictions where Waivera operates, including but not
              limited to the states of Louisiana and Florida.
              In the event of any conflict between HIPAA and state laws, the stricter standard for
              data privacy and security shall apply. Any disputes arising under this Agreement
              shall be resolved in accordance with the applicable laws of Louisiana or Florida, as
              determined by the user’s location and relevant regulatory requirements.
            </p>
          </div>
        </div>
        
        {/* Contact Information Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-envelope text-pink-500"></i> Contact Information
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              For any questions, concerns, or requests regarding data privacy, security, or
              compliance under this Agreement, users may contact Waivera at:
            </p>
            <ul className="list-none space-y-2">
              <li><strong>Email:</strong> notifications@waivera.com</li>
              <li><strong>Address:</strong> 10201 Park Rowe Avenue, Unit 2324, Baton Rouge, LA 70810</li>
            </ul>
            <p>
              Users should direct all HIPAA-related inquiries, PHI access or deletion requests, and
              breach notifications to the provided contact details. Waivera will respond in
              accordance with applicable HIPAA regulations and state laws.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProcessingAgreement;
